'use client'

import { useSyncExternalStore } from 'react'

/**
 * Whether the visitor is driving this page with a finger rather than a mouse.
 *
 * Same signal the limit wall's QR handoff already gates on in pure CSS
 * (`[@media(pointer:fine)]`), read in JS for the two cases a style rule cannot
 * express: DOM ORDER and analytics properties. `pointer: coarse` beats a width
 * query because it stays false on a resized desktop window and true on a
 * tablet, which a breakpoint gets backwards in both directions.
 *
 * useSyncExternalStore with an explicit server snapshot of `false`, so SSR and
 * the hydration render emit the desktop arrangement and a phone corrects on
 * the first client render without a mismatch.
 */

const COARSE_POINTER_QUERY = '(pointer: coarse)'

function mediaQueryList(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  try {
    return window.matchMedia(COARSE_POINTER_QUERY)
  } catch {
    return null
  }
}

function subscribe(onStoreChange: () => void): () => void {
  const mql = mediaQueryList()
  if (!mql) return () => {}
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', onStoreChange)
    return () => mql.removeEventListener('change', onStoreChange)
  }
  // Safari < 14 carries only the deprecated listener pair.
  mql.addListener(onStoreChange)
  return () => mql.removeListener(onStoreChange)
}

function getSnapshot(): boolean {
  return mediaQueryList()?.matches ?? false
}

function getServerSnapshot(): boolean {
  return false
}

export function useCoarsePointer(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/**
 * Direct read, for gates that are NOT render output.
 *
 * The hook reports `false` on the hydration render so the client markup
 * matches the server's. That is right for markup and wrong for an impression
 * property sampled in a mount effect, which would label a phone's layout as
 * the desktop one. Never use this to decide markup.
 */
export function coarsePointerNow(): boolean {
  return getSnapshot()
}
